import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface CodeExampleProps {
  code: string;
  language: 'bash' | 'json' | 'javascript' | 'typescript' | 'yaml';
  className?: string;
  showLineNumbers?: boolean;
}

export function CodeExample({ code, language, className, showLineNumbers = false }: CodeExampleProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const lines = code.split('\n');

  const getLanguageColor = () => {
    switch (language) {
      case 'bash':
        return 'text-green-400';
      case 'json':
        return 'text-amber-400';
      case 'javascript':
      case 'typescript':
        return 'text-blue-400';
      case 'yaml':
        return 'text-purple-400';
      default:
        return 'text-foreground';
    }
  };

  return (
    <div className={cn('relative rounded-lg bg-muted border', className)}>
      <div className="flex items-center justify-between border-b px-4 py-2 bg-muted/50">
        <span className={cn('text-xs font-medium uppercase', getLanguageColor())}>
          {language}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCopy}
          className="h-7 px-2 text-xs"
        >
          {copied ? (
            <>
              <svg
                className="mr-1 h-3 w-3 text-green-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg
                className="mr-1 h-3 w-3"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                />
              </svg>
              Copy
            </>
          )}
        </Button>
      </div>
      <pre className="overflow-x-auto p-4 text-sm">
        <code className={getLanguageColor()}>
          {showLineNumbers ? (
            <table className="w-full border-collapse">
              <tbody>
                {lines.map((line, index) => (
                  <tr key={index} className="hover:bg-muted/50">
                    <td className="pr-4 text-right text-muted-foreground select-none border-r border-muted-foreground/20 w-8">
                      {index + 1}
                    </td>
                    <td className="pl-4">
                      {line || ' '}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            code
          )}
        </code>
      </pre>
    </div>
  );
}